import { useEffect, useMemo, useState } from 'react';
import { useVault } from '../lib/vault';
import { api } from '../lib/api';
import { buildForecast, itemsNeedingDates } from '@shared/forecast';
import { CYCLE_LABEL, annualMoney, formatMoney, periodMoney, sumMoney } from '@shared/money';
import { plural } from '@shared/text';
import type { CostChange, FixedCost } from '@shared/types';
import { ItemIcon } from '../components/ItemIcon';
import { Confirm, ErrorNote, useAction } from '../components/ui';

/** Deutsche Kurzform mit Vorzeichen: +120,00 € / −45,00 €. */
function signed(cents: number): string {
  const betrag = formatMoney(Math.abs(cents));
  if (cents === 0) return betrag;
  return `${cents > 0 ? '+' : '−'}${betrag}`;
}

function Veraenderung({ cents }: { cents: number }): JSX.Element {
  const farbe = cents > 0 ? 'var(--danger)' : cents < 0 ? 'var(--ok)' : 'var(--muted)';
  return (
    <span className="mono" style={{ color: farbe }}>
      {signed(cents)}
    </span>
  );
}

/** Bringt einen Kostenposten in die Form, die die Prognose versteht. */
function alsVertrag(cost: FixedCost): Parameters<typeof buildForecast>[0][number] {
  return {
    id: cost.id,
    title: cost.title,
    hasCost: true,
    status: cost.status === 'active' ? 'active' : 'cancelled',
    cycle: cost.cycle,
    priceCents: cost.amountCents,
    currency: cost.currency,
    priceIsPerSeat: false,
    priceIsNet: cost.priceIsNet,
    vatRatePercent: cost.vatRatePercent,
    seats: null,
    billingDay: cost.billingDay,
    // Bei Steuern zählt die Fälligkeit, ein Verlängerungsdatum gibt es oft nicht.
    renewalDate: cost.dueDate ?? cost.renewalDate,
    purchaseDate: cost.dueDate,
    createdAt: cost.createdAt,
  } as never;
}

function Prognose({ fixed }: { fixed: FixedCost[] }): JSX.Element {
  const { items } = useVault();
  const [monate, setMonate] = useState(12);
  const [offen, setOffen] = useState<string | null>(null);
  const [mitZugaengen, setMitZugaengen] = useState(true);
  const [mitSonstigen, setMitSonstigen] = useState(true);

  // Rechnet bei jeder Änderung neu - es gibt nichts Zwischengespeichertes,
  // das veralten könnte.
  const quelle = useMemo(
    () => [
      ...(mitZugaengen ? items : []),
      ...(mitSonstigen ? fixed.map(alsVertrag) : []),
    ],
    [items, fixed, mitZugaengen, mitSonstigen],
  );
  const forecast = useMemo(() => buildForecast(quelle, monate), [quelle, monate]);
  const luecken = useMemo(() => itemsNeedingDates(quelle), [quelle]);

  const summe = forecast.reduce((s, m) => s + m.grossCents, 0);
  const max = Math.max(1, ...forecast.map((m) => m.grossCents));
  const schnitt = Math.round(summe / Math.max(1, forecast.length));
  const teuerster = [...forecast].sort((a, b) => b.grossCents - a.grossCents)[0];

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row">
          <strong className="small">Was fließt ein:</strong>
          <label className="check">
            <input
              type="checkbox"
              checked={mitZugaengen}
              onChange={(e) => setMitZugaengen(e.target.checked)}
            />
            Zugänge ({items.filter((i) => i.hasCost && i.status === 'active').length})
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={mitSonstigen}
              onChange={(e) => setMitSonstigen(e.target.checked)}
            />
            Sonstige Kosten ({fixed.filter((c) => c.status === 'active').length})
          </label>
          <span className="spacer" />
          {mitZugaengen && mitSonstigen ? (
            <span className="badge badge-accent">alle Fixkosten</span>
          ) : null}
        </div>
      </div>

      <div className="grid grid-kpi">
        <div className="card">
          <div className="kpi-label">Nächste {monate} Monate</div>
          <div className="kpi-value">{formatMoney(summe)}</div>
        </div>
        <div className="card">
          <div className="kpi-label">Durchschnitt</div>
          <div className="kpi-value">{formatMoney(schnitt)}</div>
          <div className="kpi-hint">pro Monat über den Zeitraum</div>
        </div>
        <div className="card">
          <div className="kpi-label">Teuerster Monat</div>
          <div className="kpi-value">{teuerster ? formatMoney(teuerster.grossCents) : '–'}</div>
          <div className="kpi-hint">{teuerster?.label ?? 'keine Daten'}</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-title">
          <h2>Was wann abgeht</h2>
          <div className="row-tight">
            {[6, 12, 24].map((n) => (
              <button
                key={n}
                className="btn-sm"
                aria-pressed={monate === n}
                style={monate === n ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined}
                onClick={() => setMonate(n)}
              >
                {n} Monate
              </button>
            ))}
          </div>
        </div>

        <div className="stack">
          {forecast.map((m) => (
            <div key={m.key}>
              <button
                className="month-row"
                aria-expanded={offen === m.key}
                onClick={() => setOffen(offen === m.key ? null : m.key)}
              >
                <span className="month-name">
                  {m.label}
                  {m.hasEstimates ? (
                    <span className="badge badge-warn" style={{ marginLeft: 6 }}>
                      geschätzt
                    </span>
                  ) : null}
                </span>
                <span className="month-bar">
                  <span
                    className="bar-fill"
                    style={{ width: `${Math.max(m.grossCents > 0 ? 2 : 0, (m.grossCents / max) * 100)}%` }}
                  />
                </span>
                <span className="mono month-sum">{formatMoney(m.grossCents)}</span>
              </button>

              {offen === m.key ? (
                <div className="month-detail">
                  {m.charges.length === 0 ? (
                    <p className="small muted" style={{ margin: 0 }}>
                      In diesem Monat wird nichts fällig.
                    </p>
                  ) : (
                    <table>
                      <tbody>
                        {m.charges.map((c, i) => {
                          const item = items.find((x) => x.id === c.itemId);
                          const posten = (id: string): FixedCost | undefined =>
                            fixed.find((f) => f.id === id);
                          return (
                            <tr key={`${c.itemId}-${c.date}-${i}`}>
                              <td style={{ width: 90 }} className="small muted mono">
                                {c.date.slice(8)}.{c.date.slice(5, 7)}.
                                {c.estimated ? <span title="geschätztes Datum"> *</span> : null}
                              </td>
                              <td>
                                <div className="row-tight">
                                  <ItemIcon
                                    item={item ?? posten(c.itemId) ?? { iconSymbol: '', iconData: '', title: c.title }}
                                    size={20}
                                  />
                                  <div>
                                    <strong>{c.title}</strong>
                                    <div className="small muted">{CYCLE_LABEL[c.cycle]}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="num mono">{formatMoney(c.grossCents)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {luecken.length > 0 ? (
        <div className="note note-warn" style={{ marginTop: 14 }}>
          <strong>
            Bei {plural(luecken.length, 'Vertrag fehlt', 'Verträgen fehlen')} das Datum – diese
            Termine sind geraten.
          </strong>
          <div className="small" style={{ marginTop: 6 }}>
            {luecken.slice(0, 8).map((i) => i.title).join(', ')}
            {luecken.length > 8 ? ' …' : ''}
          </div>
          <p className="small" style={{ margin: '8px 0 0' }}>
            Trag beim Eintrag den Abbuchungstag oder das Verlängerungsdatum nach, dann stimmt die
            Prognose auf den Tag genau.
          </p>
        </div>
      ) : null}
    </>
  );
}

function Jahresrueckblick(): JSX.Element {
  const { items } = useVault();
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [changes, setChanges] = useState<CostChange[]>([]);
  const [jahre, setJahre] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [zuruecksetzen, setZuruecksetzen] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [neuLaden, setNeuLaden] = useState(0);

  useEffect(() => {
    let abgebrochen = false;
    setLoading(true);
    api
      .costHistory(jahr)
      .then((r) => {
        if (abgebrochen) return;
        setChanges(r.changes);
        setJahre(r.years);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!abgebrochen) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!abgebrochen) setLoading(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, [jahr, neuLaden]);

  const bilanz = useMemo(() => {
    const mitDelta = changes.map((c) => ({
      ...c,
      delta: c.afterAnnualCents - c.beforeAnnualCents,
    }));
    const teurer = mitDelta.filter((c) => c.delta > 0).sort((a, b) => b.delta - a.delta);
    const guenstiger = mitDelta.filter((c) => c.delta < 0).sort((a, b) => a.delta - b.delta);
    const neu = mitDelta.filter((c) => c.kind === 'created');
    const weg = mitDelta.filter((c) => c.kind === 'removed');
    return {
      mitDelta,
      teurer,
      guenstiger,
      neu,
      weg,
      summeTeurer: teurer.reduce((s, c) => s + c.delta, 0),
      summeGuenstiger: guenstiger.reduce((s, c) => s + c.delta, 0),
      netto: mitDelta.reduce((s, c) => s + c.delta, 0),
    };
  }, [changes]);

  const aktuell = sumMoney(
    items.filter((i) => i.status === 'active').map(annualMoney),
  ).grossCents;

  return (
    <>
      <div className="card-title" style={{ margin: '24px 0 10px' }}>
        <h2>Was sich verändert hat</h2>
        <div className="row-tight">
          {changes.length > 0 ? (
            <button className="btn-quiet btn-sm" onClick={() => setZuruecksetzen(true)}>
              Verlauf zurücksetzen
            </button>
          ) : null}
          <select
            value={jahr}
            onChange={(e) => setJahr(Number(e.target.value))}
            style={{ maxWidth: 130 }}
          >
            {(jahre.length > 0 ? jahre : [jahr]).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ErrorNote error={error} />
      {hinweis ? <div className="note note-ok">{hinweis}</div> : null}

      {loading ? (
        <div className="empty">Wird geladen …</div>
      ) : changes.length === 0 ? (
        <div className="card">
          <div className="empty">
            Für {jahr} sind keine Kostenänderungen aufgezeichnet.
            <div className="small" style={{ marginTop: 8 }}>
              Aufgezeichnet wird ab jetzt: jedes Anlegen, jede Preisänderung, jede Kündigung. Der
              Rückblick füllt sich also im Lauf des Jahres von selbst.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-kpi">
            <div className="card">
              <div className="kpi-label">Netto-Veränderung</div>
              <div className="kpi-value" style={{ color: bilanz.netto > 0 ? 'var(--danger)' : 'var(--ok)' }}>
                {signed(bilanz.netto)}
              </div>
              <div className="kpi-hint">
                pro Jahr, gegenüber dem Stand zu Jahresbeginn
              </div>
            </div>
            <div className="card">
              <div className="kpi-label">Teurer geworden</div>
              <div className="kpi-value" style={{ color: 'var(--danger)' }}>
                {signed(bilanz.summeTeurer)}
              </div>
              <div className="kpi-hint">
                {plural(bilanz.teurer.length, 'Erhöhung', 'Erhöhungen')} inkl. Neuzugänge
              </div>
            </div>
            <div className="card">
              <div className="kpi-label">Gespart</div>
              <div className="kpi-value" style={{ color: 'var(--ok)' }}>
                {signed(bilanz.summeGuenstiger)}
              </div>
              <div className="kpi-hint">
                {plural(bilanz.guenstiger.length, 'Senkung', 'Senkungen')} inkl. Kündigungen
              </div>
            </div>
            <div className="card">
              <div className="kpi-label">Stand heute</div>
              <div className="kpi-value">{formatMoney(aktuell)}</div>
              <div className="kpi-hint">laufende Kosten aufs Jahr</div>
            </div>
          </div>

          <div className="grid grid-2" style={{ marginTop: 14 }}>
            <div className="card">
              <div className="card-title">
                <h2>Was teurer wurde</h2>
                <span className="small muted">größte zuerst</span>
              </div>
              {bilanz.teurer.length === 0 ? (
                <div className="empty">Nichts ist teurer geworden.</div>
              ) : (
                <table>
                  <tbody>
                    {bilanz.teurer.slice(0, 12).map((c) => (
                      <tr key={c.id}>
                        <td>
                          <strong>{c.title}</strong>
                          <div className="small muted">
                            {c.note || (c.kind === 'created' ? 'neu hinzugekommen' : '')} ·{' '}
                            {new Date(c.at).toLocaleDateString('de-DE')}
                          </div>
                        </td>
                        <td className="num">
                          <Veraenderung cents={c.delta} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="card">
              <div className="card-title">
                <h2>Wo gespart wurde</h2>
                <span className="small muted">größte zuerst</span>
              </div>
              {bilanz.guenstiger.length === 0 ? (
                <div className="empty">Noch nichts gespart.</div>
              ) : (
                <table>
                  <tbody>
                    {bilanz.guenstiger.slice(0, 12).map((c) => (
                      <tr key={c.id}>
                        <td>
                          <strong>{c.title}</strong>
                          <div className="small muted">
                            {c.note || (c.kind === 'removed' ? 'gelöscht' : '')} ·{' '}
                            {new Date(c.at).toLocaleDateString('de-DE')}
                          </div>
                        </td>
                        <td className="num">
                          <Veraenderung cents={c.delta} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <div className="card-title">
              <h2>Alle Änderungen in {jahr}</h2>
              <span className="small muted">{plural(changes.length, 'Eintrag', 'Einträge')}</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Wann</th>
                  <th>Dienst</th>
                  <th>Was</th>
                  <th className="num">aufs Jahr</th>
                </tr>
              </thead>
              <tbody>
                {bilanz.mitDelta.map((c) => (
                  <tr key={c.id}>
                    <td className="small muted" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(c.at).toLocaleDateString('de-DE')}
                    </td>
                    <td>
                      <strong>{c.title}</strong>
                      <div className="small muted">{c.actorEmail}</div>
                    </td>
                    <td className="small">
                      {c.kind === 'created' ? (
                        <span className="badge badge-ok">neu</span>
                      ) : c.kind === 'removed' ? (
                        <span className="badge">gelöscht</span>
                      ) : null}{' '}
                      {c.note}
                    </td>
                    <td className="num">
                      <Veraenderung cents={c.delta} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {zuruecksetzen ? (
        <Confirm
          danger
          title="Verlauf zurücksetzen?"
          confirmLabel="Zurücksetzen"
          message={
            <>
              <p>
                Alle {changes.length} aufgezeichneten Änderungen werden gelöscht. Ab dann beginnt
                der Rückblick bei null – der heutige Stand gilt als Ausgangspunkt.
              </p>
              <p className="muted">
                <strong>Deine Einträge bleiben unberührt.</strong> Gelöscht wird nur die
                Aufzeichnung dessen, was sich verändert hat – keine Zugänge, keine Kostenposten,
                keine Passwörter.
              </p>
              <p className="muted">
                Gedacht für genau einen Moment: wenn die Ersterfassung abgeschlossen ist. Sonst
                stünde dort eine Kostensteigerung von mehreren tausend Euro, obwohl nur
                aufgeschrieben wurde, was ohnehin schon lief.
              </p>
            </>
          }
          onConfirm={async () => {
            const { deleted } = await api.clearCostHistory();
            setHinweis(
              `${deleted} Aufzeichnungen gelöscht. Der Rückblick beginnt ab jetzt beim heutigen Stand.`,
            );
            setNeuLaden((n) => n + 1);
          }}
          onClose={() => setZuruecksetzen(false)}
        />
      ) : null}
    </>
  );
}

export function ForecastPage(): JSX.Element {
  const [fixed, setFixed] = useState<FixedCost[]>([]);

  useEffect(() => {
    let abgebrochen = false;
    api
      .fixedCosts()
      .then((c) => {
        if (!abgebrochen) setFixed(c);
      })
      .catch(() => undefined);
    return () => {
      abgebrochen = true;
    };
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Prognose</h1>
        </div>
      </div>

      <Prognose fixed={fixed} />
      <Jahresrueckblick />
    </>
  );
}
